import { addDays, addMinutes, addMonths, addWeeks, differenceInMinutes, endOfDay, format, formatISO, getDate, getMonth, getWeekOfMonth, isSameDay, isWeekend, parse, parseISO, setDay, startOfDay, startOfMonth, startOfWeek, } from "date-fns";
import { enUS } from "date-fns/locale";
import { uid } from "./uid";
import { getDeepCopy, isDeepEqual } from "./helpers";
// default object
const defaultRRule = {
    freq: "NEVER",
    FREQ: "DAILY",
    INTERVAL: 1,
    BYDAY: [],
    BYMONTH: 1,
    BYMONTHDAY: 1,
    UNTIL: null,
    COUNT: 1,
    EXDATE: [],
    ends: "NEVER",
    byset: "DAY",
};
export function icalFormat(date) {
    return formatISO(date).split("+")[0];
}
export function dateFromIcal(str) {
    return parseISO(str);
}
// Removes unnecessary parts of pattern according to rule
export function normalizeRRULEObj(config) {
    const { weekDays, freq, ends, EXDATE, COUNT, UNTIL, byset, ...rest } = config;
    config = rest;
    let normalize = {};
    switch (freq) {
        case "CUSTOM": {
            for (const key in config) {
                const value = config[key];
                if (!value)
                    continue;
                normalize[key] = value;
            }
            break;
        }
        case "WORKDAYS": {
            const date = new Date();
            const workdays = weekDays
                .filter(d => !isWeekend(parse(d.id, "EEEEEE", date)))
                .map(d => d.id);
            normalize = { INTERVAL: 1, BYDAY: workdays, FREQ: "WEEKLY" };
            break;
        }
        case "NEVER":
            break;
        default:
            normalize.FREQ = freq;
    }
    switch (normalize.FREQ) {
        case "WEEKLY": {
            const { BYMONTHDAY, BYSETPOS, BYMONTH, BYPOS, ...rest } = normalize;
            normalize = { ...rest };
            break;
        }
        case "DAILY": {
            const { BYDAY, BYSETPOS, BYMONTH, BYMONTHDAY, BYPOS, ...rest } = normalize;
            normalize = { ...rest };
            break;
        }
        case "MONTHLY": {
            const { BYDAY, BYSETPOS, BYMONTH, BYMONTHDAY, ...rest } = normalize;
            normalize = { ...rest };
            if (byset === "DAY") {
                normalize = { ...normalize, BYMONTHDAY };
            }
            else {
                normalize = { ...normalize, BYSETPOS, BYDAY };
            }
            break;
        }
        case "YEARLY": {
            const { BYDAY, BYMONTHDAY, BYSETPOS, ...rest } = normalize;
            normalize = { ...rest };
            if (byset === "DAY") {
                normalize = { ...normalize, BYMONTHDAY };
            }
            else {
                normalize = { ...normalize, BYSETPOS, BYDAY };
            }
            break;
        }
    }
    switch (ends) {
        case "ON": {
            normalize = { ...normalize, UNTIL: icalFormat(UNTIL) };
            break;
        }
        case "AFTER": {
            normalize = { ...normalize, COUNT };
            break;
        }
    }
    if (EXDATE.length) {
        normalize.EXDATE = EXDATE.map(d => d && icalFormat(d)).join(",");
    }
    return normalize;
}
// Converts  IRRULEObj to string
export function serializeRecurring(config) {
    const cnf = normalizeRRULEObj(config);
    const str = [];
    for (const key in config) {
        const value = cnf[key];
        if (value)
            str.push(`${key}=${value}`);
    }
    return str.join(";");
}
export function getRRULEConstByDate(date) {
    const BYDAY = [format(date, "EEEEEE")];
    const BYMONTH = getMonth(date) + 1;
    const BYMONTHDAY = getDate(date);
    const BYSETPOS = getWeekOfMonth(date);
    return { BYDAY, BYMONTH, BYMONTHDAY, BYSETPOS };
}
//Converts string to IRRULEObj
export function parseRecurring(string, event, _dates = enUS, includeDefaults = true) {
    if (typeof string === "object" && string)
        return string;
    const { start_date, end_date } = event;
    const { BYDAY, BYMONTH, BYMONTHDAY, BYSETPOS } = getRRULEConstByDate(start_date);
    const config = includeDefaults
        ? {
            ...defaultRRule,
            weekDays: getWeekDays(_dates),
            BYDAY,
            BYMONTH,
            BYSETPOS,
            BYMONTHDAY,
            UNTIL: startOfDay(addDays(end_date, 1)),
        }
        : { weekDays: getWeekDays(_dates) };
    if (!string) {
        return config;
    }
    //FREQ=DAILY;INTERVAL=2
    const parts = string.split(";");
    for (let i = 0; i < parts.length; i++) {
        const splited = parts[i].split("=");
        const code = splited[0];
        let name = splited[1];
        if (code === "UNTIL") {
            name = dateFromIcal(name);
            config.ends = "ON";
        }
        if (code === "EXDATE") {
            name = name.split(",").map((d) => dateFromIcal(d));
        }
        if (code === "BYDAY") {
            name = name.split(",");
        }
        if (code === "COUNT") {
            config.ends = "AFTER";
        }
        if (code === "BYSETPOS") {
            config.byset = "POS";
        }
        config[code] = name;
    }
    if (parts.length === 1 && parts[0].startsWith("FREQ")) {
        config.freq = config.FREQ;
    }
    if (parts.length > 1) {
        config.freq = "CUSTOM";
    }
    return config;
}
export function getWeekDays(locale) {
    const weekDays = [];
    let weekStart = startOfWeek(new Date(), { locale });
    for (let i = 0; i < 7; i++) {
        const name = format(weekStart, "EEEEEE", { locale });
        const id = format(weekStart, "EEEEEE");
        const fullName = format(weekStart, "EEEE", { locale });
        weekDays.push({ id, name, index: i, fullName });
        weekStart = addDays(weekStart, 1);
    }
    return weekDays;
}
// Returns the last date of pattern
export function getLastDate(rule, event) {
    const start = event.STDATE || event.start_date;
    const { COUNT, UNTIL, ends } = rule;
    switch (ends) {
        case "ON":
            return UNTIL;
        case "AFTER": {
            let currentDate = new Date(start);
            const wdays = fillWdays(rule);
            for (let i = 1; i < COUNT; i++) {
                currentDate = next(currentDate, rule, wdays);
            }
            return currentDate;
        }
        default:
            return new Date("9999-12-31T00:00:00");
    }
}
export function next(date, mode, wdays) {
    const { FREQ, INTERVAL = 1, BYMONTHDAY, BYSETPOS, BYDAY, weekDays } = mode;
    let interval = INTERVAL * 1;
    if (FREQ == "YEARLY")
        interval *= 12;
    let endDate = new Date(date);
    let now, step;
    switch (FREQ) {
        case "DAILY":
            endDate = addDays(endDate, interval);
            break;
        case "WEEKLY":
            now = endDate.getDay();
            step = wdays ? wdays[now] : 7;
            if (step + now > 6 && interval > 1) {
                // when new day is on the different week
                // add week interval as well
                step += 7 * (interval - 1);
            }
            endDate = addDays(endDate, step);
            break;
        case "YEARLY":
        case "MONTHLY":
            now = endDate.getMonth();
            endDate = addMonths(endDate, interval);
            if (BYMONTHDAY) {
                endDate.setDate(BYMONTHDAY);
                // when date is outside of month bounds, switch to last day of month
                if ((now + interval) % 12 != endDate.getMonth())
                    endDate.setDate(0);
            }
            else if (BYSETPOS) {
                // go to first day of month;
                endDate = startOfMonth(endDate);
                // set week
                endDate = addWeeks(endDate, +BYSETPOS - 1);
                // set BYDAY
                endDate = setDay(endDate, weekDays.find(({ id }) => BYDAY[0] === id).index);
            }
            break;
    }
    return endDate;
}
export function fillWdays(mode) {
    const wdays = [0, 0, 0, 0, 0, 0, 0];
    const { BYDAY, weekDays } = mode;
    if (BYDAY) {
        const diff = wdays;
        const line = BYDAY.map(a => weekDays.findIndex(i => i.id === a)).sort();
        // for each allowed day, fill range with steps
        for (let i = 0; i < line.length; i++) {
            const s = line[i];
            const e = line[i + 1] || line[0] + 7;
            for (let j = s; j < e; j++) {
                diff[j % 7] = e - j;
            }
        }
        return diff;
    }
}
// Converts recurring event into array of events
export function explodeEvent(sdate, edate, ev, _dates = enUS) {
    const mode = parseRecurring(ev.RRULE, ev, _dates, false);
    const { COUNT, UNTIL, EXDATE } = mode;
    const until = UNTIL?.valueOf();
    const { start_date, end_date, id } = ev;
    const res = [];
    // when we use week-days in recurring mode, convert structure
    // list of days => list of steps to the next allowed day
    const wdays = fillWdays(mode);
    let xdate = new Date(start_date);
    let xdateValue = xdate.valueOf();
    const length = end_date.valueOf() - xdateValue;
    // occurrences before active window
    let count = 1;
    while ((sdate.valueOf() - xdateValue >= (length || 1) &&
        (!UNTIL || xdateValue < until) &&
        (!COUNT || count <= COUNT)) ||
        findDate(EXDATE, xdate)) {
        ++count;
        xdate = next(xdate, mode, wdays);
        xdateValue = xdate.valueOf();
    }
    // occurrences inside of window
    while (edate.valueOf() > xdateValue &&
        (!UNTIL || xdateValue < until) &&
        (!COUNT || count <= COUNT)) {
        // make copy of recurring event
        if (!findDate(EXDATE, xdate)) {
            const copy = getDeepCopy(ev);
            const x = new Date(xdate).setHours(xdate.getHours());
            copy.start_date = new Date(x);
            copy.end_date = new Date(x * 1 + length);
            if (!isDeepEqual(copy, ev)) {
                copy.parent_id = id;
                copy.id = copy.start_date.valueOf() + "wx_recurring" + id;
            }
            res.push(copy);
        }
        count++;
        xdate = next(xdate, mode, wdays);
        xdateValue = xdate.valueOf();
    }
    return res;
}
export function findDate(arr, date) {
    if (!arr)
        return false;
    const res = arr.find(d => isSameDay(d, date));
    return !!res;
}
export function getRecurringById(id, events) {
    const [date, parent_id] = `${id}`.split("wx_recurring");
    const parent = events.find(item => item.id == parent_id);
    const event = explodeEvent(startOfDay(+date), endOfDay(+date), parent);
    return event[0];
}
export function updateRecurring(event, initEvent, action, mode, dispatch) {
    const actionData = { action };
    const { parent_id, id, start_date, end_date } = event;
    const { start_date: sd, end_date: ed } = initEvent || {};
    // change mode to "all" if event is parent
    if (mode === "future" && sd.valueOf() === initEvent.STDATE.valueOf()) {
        mode = "all";
    }
    let newEvent;
    switch (mode) {
        case "all": {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { start_date: _s, end_date: _e, ...rest } = event;
            const newParent = { ...rest };
            const prevId = parent_id || id;
            if (action === "delete-event") {
                return dispatch("action", { action, data: { id: prevId } });
            }
            if (start_date.valueOf() !== sd.valueOf() ||
                ed.valueOf() !== end_date.valueOf()) {
                // update parent dates
                newParent.start_date = addMinutes(event.STDATE, differenceInMinutes(start_date, sd));
                newParent.end_date = addMinutes(new Date(newParent.start_date), differenceInMinutes(end_date, start_date));
                // update rrule start date
                newParent.STDATE = newParent.start_date;
            }
            // set parent_id as main id
            newParent.id = prevId;
            actionData.data = { id: prevId, event: newParent };
            dispatch("action", actionData);
            break;
        }
        case "only": {
            const prevId = parent_id || id;
            const RRULEObj = parseRecurring(initEvent.RRULE, event);
            const prevEXDATE = RRULEObj?.EXDATE || [];
            const EXDATE = [...prevEXDATE, startOfDay(sd)];
            const newRRULE = RRULEObj
                ? serializeRecurring({ ...RRULEObj, EXDATE })
                : null;
            // generate new rule
            const newParentEvent = {
                id: prevId,
                RRULE: newRRULE,
            };
            delete newParentEvent.parent_id;
            const data = { id: prevId, event: newParentEvent };
            dispatch("action", { action: "update-event", data });
            if (action === "update-event" && event.recurring) {
                newEvent = {
                    ...cleanEventFromRecurring(event),
                    id: uid(),
                };
                dispatch("action", {
                    action: "add-event",
                    data: { event: newEvent },
                });
            }
            break;
        }
        case "future": {
            const prevId = parent_id || id;
            const RRULEObj = parseRecurring(initEvent.RRULE, initEvent);
            const newRRULE = serializeRecurring({
                ...RRULEObj,
                UNTIL: startOfDay(sd),
                ends: "ON",
            });
            // generate new rule
            const newParentEvent = {
                id: prevId,
                RRULE: newRRULE,
                DTEND: startOfDay(sd),
            };
            delete newParentEvent.parent_id;
            const data = { id: prevId, event: newParentEvent };
            dispatch("action", { action: "update-event", data });
            if (action === "update-event" && event.recurring) {
                // add new event
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { parent_id, ...rest } = event;
                newEvent = {
                    ...rest,
                    id: uid(),
                    parent_id: null,
                    STDATE: start_date,
                };
                dispatch("action", {
                    action: "add-event",
                    data: { event: newEvent },
                });
            }
            break;
        }
    }
}
function cleanEventFromRecurring(event) {
    const { RRULE, STDATE, DTEND, EXDATE, parent_id, ...rest } = event;
    return { ...rest, recurring: false };
}
//# sourceMappingURL=recurring.js.map